/**
 * ============================ STUB — NOT A REAL BACKEND ======================
 * Community sharing for pilotage plans is SCAFFOLDING ONLY. There is no server,
 * no accounts, and no cross-user storage. Every function here resolves LOCALLY:
 * sharing and rating are accepted and thrown away, and the "discovery" list is a
 * small set of clearly-labelled SAMPLE plans baked into the app, not other users'
 * data. Nothing a user does here is persisted for anyone else to see.
 *
 * When a real backend exists, replace the bodies below (keep the signatures) with
 * actual network calls and delete the SAMPLE_PLANS. Everything is marked so the
 * UI can show honest "demo / not connected" badges.
 * ============================================================================
 */
import { emptyPlan, type PilotagePlanDoc, type LonLat } from './plannerModel';
import { makePlannerId } from '@/store/pilotagePlanner';

/** Flip to true only when a real backend is wired in. */
export const COMMUNITY_BACKEND_CONNECTED = false;

/** A short, honest banner the UI shows wherever community features appear. */
export const COMMUNITY_STUB_NOTICE =
  'Community sharing is a preview — there is no server yet, so sharing and ratings are not sent and the plans below are samples.';

export interface CommunityPlanSummary {
  id: string;
  name: string;
  author: string;
  /** Average rating 0–5 (sample figure). */
  ratingAvg: number;
  ratingCount: number;
  /** How many people have loaded it (sample figure). */
  uses: number;
  description: string;
  /** A human area name and rough bounds, for the "near here" count. */
  area: string;
  bounds: [number, number, number, number];
}

interface StubResult {
  ok: boolean;
  /** Always true here — a reminder to the caller/UI that nothing left the device. */
  stub: true;
  message: string;
}

/** Clearly-labelled SAMPLE community plans (NOT real user data). */
const SAMPLE_PLANS: CommunityPlanSummary[] = [
  {
    id: 'sample-hinder',
    name: 'Hinder Sound — first-timer approach',
    author: 'Sample · Ardent crew',
    ratingAvg: 4.8,
    ratingCount: 37,
    uses: 214,
    description: 'Wheel-overs on the two beacons, a transit into the pool, and the 5 m line marked as a clearing depth. Good in any tide.',
    area: 'Hinder Sound',
    bounds: [-5.66, 46.28, -5.54, 46.34],
  },
  {
    id: 'sample-parvin',
    name: 'Parvin Sound night entry',
    author: 'Sample · night owl',
    ratingAvg: 4.2,
    ratingCount: 12,
    uses: 63,
    description: 'Back bearing off the light, leading line on the range, and notes for the marina VHF and the fuel berth.',
    area: 'Parvin Sound',
    bounds: [-5.9, 46.18, -5.78, 46.24],
  },
  {
    id: 'sample-mallin',
    name: 'Mallin Bay anchoring plan',
    author: 'Sample · weekend sailor',
    ratingAvg: 3.9,
    ratingCount: 8,
    uses: 41,
    description: 'Route to the anchorage following the 8 m contour, with a clearing bearing off the headland and a note on the holding.',
    area: 'Mallin Bay',
    bounds: [-5.98, 46.36, -5.86, 46.44],
  },
];

const delay = <T,>(value: T, ms = 240): Promise<T> => new Promise((res) => setTimeout(() => res(value), ms));

/** [STUB] "Share" a plan with one person by email. Nothing is sent. */
export function shareByEmail(planId: string, email: string): Promise<StubResult> {
  console.warn('[community STUB] shareByEmail is not wired to a backend', { planId, email });
  return delay({ ok: true, stub: true, message: `Preview only — “${email}” was not actually sent this plan.` });
}

/** [STUB] "Publish" a plan publicly. Nothing is published. */
export function sharePublicly(planId: string): Promise<StubResult> {
  console.warn('[community STUB] sharePublicly is not wired to a backend', { planId });
  return delay({ ok: true, stub: true, message: 'Preview only — nothing was published; this needs the backend.' });
}

/** [STUB] "Rate" a community plan. The tap is accepted locally but goes nowhere. */
export function ratePlan(planId: string, stars: number): Promise<StubResult> {
  console.warn('[community STUB] ratePlan is not wired to a backend', { planId, stars });
  return delay({ ok: true, stub: true, message: 'Preview only — your rating was not sent.' });
}

/** [STUB] The discovery list, ranked by rating then popularity. Sample data only. */
export function listCommunityPlans(sort: 'rating' | 'popularity' = 'rating'): Promise<CommunityPlanSummary[]> {
  const ranked = [...SAMPLE_PLANS].sort((a, b) =>
    sort === 'popularity' ? b.uses - a.uses : b.ratingAvg - a.ratingAvg || b.ratingCount - a.ratingCount,
  );
  return delay(ranked);
}

/** [STUB] Count of public plans over a chart area (from the sample set within bounds). */
export function publicPlansCountForArea(bounds: [number, number, number, number] | null): number {
  if (!bounds) return SAMPLE_PLANS.length;
  const [minLon, minLat, maxLon, maxLat] = bounds;
  return SAMPLE_PLANS.filter((p) => {
    const [aLon, aLat, bLon, bLat] = p.bounds;
    const cx = (aLon + bLon) / 2;
    const cy = (aLat + bLat) / 2;
    return cx >= minLon && cx <= maxLon && cy >= minLat && cy <= maxLat;
  }).length;
}

/** Build a concrete plan doc from a sample summary, so "load / duplicate" works. */
export function importCommunityPlan(id: string): PilotagePlanDoc | null {
  const summary = SAMPLE_PLANS.find((p) => p.id === id);
  if (!summary) return null;
  const doc = emptyPlan(makePlannerId('pp'), `${summary.name} (from community)`, Date.now());
  // A minimal but real route across the sample's area, so it lands on the chart
  // with something to shape. (A real backend would return the author's full plan.)
  const [minLon, minLat, maxLon, maxLat] = summary.bounds;
  const a: LonLat = [minLon + (maxLon - minLon) * 0.2, minLat + (maxLat - minLat) * 0.3];
  const b: LonLat = [minLon + (maxLon - minLon) * 0.55, minLat + (maxLat - minLat) * 0.55];
  const c: LonLat = [minLon + (maxLon - minLon) * 0.8, minLat + (maxLat - minLat) * 0.75];
  doc.route.waypoints = [
    { id: makePlannerId('wp'), position: a, name: 'Approach' },
    { id: makePlannerId('wp'), position: b, name: 'Wheel-over' },
    { id: makePlannerId('wp'), position: c, name: 'Berth' },
  ];
  doc.stickyNotes = [{ id: makePlannerId('sn'), position: b, text: summary.description }];
  doc.bounds = summary.bounds;
  return doc;
}
